import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { Box, Typography, Paper } from '@mui/material';
import Colors from '../ui/Colors';

const BaseMap = ({ 
  children,
  data,
  metricField,
  colorScale,
  getFillColor,
  onHover,
  onLeave,
  tooltipContent,
  legendTitle,
  legendColors,
  legendValues
}) => {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoverContent, setHoverContent] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedCountyInfo, setSelectedCountyInfo] = useState(null);

  useEffect(() => {
    if (!data) {
      console.log('[BaseMap] No data provided');
      return;
    }

    console.log('[BaseMap] Data:', data);

    // Normalize data FIPS codes first
    const normalizedData = data.map(item => ({
      ...item,
      normalizedFips: String(item.fips).padStart(5, '0')
    }));
    
    console.log('[BaseMap] normalizedData: ', normalizedData);

    console.groupCollapsed('[BaseMap] Data Validation Report');
    console.log('[BaseMap] Received data:', {
      dataType: Array.isArray(data) ? 'array' : typeof data,
      itemCount: data.length,
      firstItem: data[0] ? { 
        fips: data[0].fips,
        paddedFips: String(data[0].fips).padStart(5, '0'),
        foodInsecurityRate: data[0].foodInsecurityRate
      } : null
    });

    const verifyMatches = async () => {
      try {
        console.log('[BaseMap] Fetching GeoJSON...');
        const response = await fetch("https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json");
        const geoData = await response.json();
        const features = geoData.objects.counties.geometries;
        console.log('[BaseMap] GeoJSON features sample:', features.slice(0, 3));

        // Check known working county (Todd County, SD - FIPS 46121)
        const testFeature = features.find(f => String(f.id) === "46121");
        const testDataMatch = normalizedData.find(d => d.normalizedFips === "46121");
        
        console.log('[BaseMap] Critical Validation Check:', {
          geoFeatureExists: !!testFeature,
          dataRecordExists: !!testDataMatch,
          featureIdType: typeof testFeature?.id,
          dataFipsType: typeof testDataMatch?.fips,
          normalizedFips: testDataMatch?.normalizedFips
        });

        // Check FIPS conversion
        console.log('[BaseMap] FIPS Conversion Check:', {
          geoFips: String(testFeature?.id).padStart(5, '0'),
          dataFips: testDataMatch?.normalizedFips
        });

        // Get county names from GeoJSON properties
        const countyNames = {};
        features.forEach(feature => {
          if (feature.properties && feature.properties.name) {
            countyNames[String(feature.id).padStart(5, '0')] = feature.properties.name;
          }
        });

        // Advanced matching with fallbacks
        const sampleMatches = features.slice(0, 5).map(feature => {
          const geoFips = String(feature.id).padStart(5, '0');
          const countyName = feature.properties?.name;
          
          // Primary match by normalized FIPS
          let dataMatch = normalizedData.find(d => d.normalizedFips === geoFips);
          
          // Fallback match by county name if available
          if (!dataMatch && countyName) {
            dataMatch = normalizedData.find(d => {
              const dataCounty = d.countyName?.replace(/ County$/, "") || "";
              return dataCounty === countyName || 
                     countyName.includes(dataCounty) || 
                     dataCounty.includes(countyName);
            });
          }
          
          console.log(`[BaseMap] Feature ${geoFips} (${countyName}) match check:`, {
            geoFips,
            countyName,
            dataFips: dataMatch?.normalizedFips || 'none',
            dataCountyName: dataMatch?.countyName || 'none',
            match: !!dataMatch,
            hasColor: !!getFillColor({ id: feature.id, properties: feature.properties })
          });

          return { geoFips, match: !!dataMatch };
        });

        console.log('[BaseMap] Validation summary:', {
          totalFeatures: features.length,
          totalData: normalizedData.length,
          sampleMatches,
          matchRate: (sampleMatches.filter(m => m.match).length / 5) * 100 + '%'
        });
        
      } catch (error) {
        console.error("[BaseMap] Validation error:", error);
      }
    };
    
    verifyMatches().finally(() => console.groupEnd());
  }, [data]);

  const handleMouseMove = (event, geo, countyData) => {
    setTooltipPosition({ x: event.clientX, y: event.clientY });
    const content = onHover(event, geo, countyData);
    setHoverContent(content);
  };

  const handleMouseLeave = () => {
    setHoverContent(null);
    if (onLeave) onLeave();
  };

  const handleCountyClick = (geo, countyData) => {
    // Toggle selection if clicking the same county
    if (selectedCounty === geo.id) {
      setSelectedCounty(null);
      setSelectedCountyInfo(null);
    } else {
      setSelectedCounty(geo.id);
      
      // Create info object with county details
      const stateFips = geo.id ? String(geo.id).substring(0, 2) : '';
      const countyName = geo.properties?.name || 'Unknown';
      
      setSelectedCountyInfo({
        fips: String(geo.id).padStart(5, '0'),
        countyName: countyName,
        stateFips: stateFips,
        data: countyData
      });
    }
  };

  // Helper function for formatting percentages in the selected county info panel
  const formatPercentage = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 'N/A';
    
    // Check if the value is already in percentage format (greater than 1)
    if (value > 1) {
      return `${Math.round(value * 10) / 10}%`; // Already a percentage, just round to 1 decimal
    } else {
      return `${Math.round(value * 1000) / 10}%`; // Convert from decimal to percentage
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <ComposableMap projection="geoAlbersUsa">
        <ZoomableGroup>
          <Geographies geography="https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json">
            {({ geographies }) => {
              console.log('[BaseMap] Rendering geographies count:', geographies?.length);
              return geographies.map(geo => {
                const fips = String(geo.id).padStart(5, '0');
                
                // Find matching data using normalized FIPS or county name fallback
                const countyData = data.find(d => {
                  const normalizedDataFips = String(d.fips).padStart(5, '0');
                  if (normalizedDataFips === fips) return true;
                  
                  // Optional: Add name-based fallback matching
                  const geoCounty = geo.properties?.name;
                  const dataCounty = d.countyName?.replace(/ County$/, "") || "";
                  return geoCounty && (
                    dataCounty === geoCounty || 
                    geoCounty.includes(dataCounty) || 
                    dataCounty.includes(geoCounty)
                  );
                });
                
                const fillColor = getFillColor(geo, countyData);
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke="#DDD"
                    strokeWidth={0.2}
                    onMouseMove={(e) => handleMouseMove(e, geo, countyData)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleCountyClick(geo, countyData)}
                    style={{
                      default: { outline: 'none', cursor: 'pointer' },
                      hover: { fill: '#3C3C3C', outline: 'none' }
                    }}
                  />
                );
              });
            }}
          </Geographies>
          
          {/* Selected County Highlight */}
          {selectedCounty && (
            <Geographies geography="https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json">
              {({ geographies }) => geographies
                .filter(geo => geo.id === selectedCounty)
                .map(geo => (
                  <Geography
                    key={geo.rsmKey + "-highlight"}
                    geography={geo}
                    fill="none"
                    stroke={Colors.primary}
                    strokeWidth={2}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" }
                    }}
                  />
                ))}
            </Geographies>
          )}
        </ZoomableGroup>
      </ComposableMap>

      {/* Selected County Info Panel */}
      {selectedCountyInfo && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '4px',
            maxWidth: '280px',
            zIndex: 1000,
            border: `2px solid ${Colors.primary}`
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {selectedCountyInfo.countyName} County
          </Typography>
          
          {selectedCountyInfo.data && (
            <Box sx={{ fontSize: '0.9rem' }}>
              {/* Food Insecurity Rate */}
              {selectedCountyInfo.data.foodInsecurityRate !== undefined && (
                <Box sx={{ mb: 0.5 }}>
                  <strong>Food Insecurity Rate:</strong> {formatPercentage(selectedCountyInfo.data.foodInsecurityRate)}
                </Box>
              )}
              
              {/* Poverty Rate */}
              {selectedCountyInfo.data.povertyRate !== undefined && (
                <Box sx={{ mb: 0.5 }}>
                  <strong>Poverty Rate:</strong> {formatPercentage(selectedCountyInfo.data.povertyRate)}
                </Box>
              )}
              
              {/* Low Income Status */}
              {selectedCountyInfo.data.lowIncomeTracts !== undefined && (
                <Box sx={{ mb: 0.5 }}>
                  <strong>Low Income Area:</strong> {selectedCountyInfo.data.lowIncomeTracts > 0 ? 'Yes' : 'No'}
                </Box>
              )}
              
              {/* Food Access */}
              {selectedCountyInfo.data.foodAccessIndex !== undefined && (
                <Box sx={{ mb: 0.5 }}>
                  <strong>Limited Food Access:</strong> {formatPercentage(selectedCountyInfo.data.foodAccessIndex)}
                </Box>
              )}
              
              {/* Population */}
              {selectedCountyInfo.data.population !== undefined && (
                <Box sx={{ mb: 0.5 }}>
                  <strong>Population:</strong> {selectedCountyInfo.data.population.toLocaleString()}
                </Box>
              )}
            </Box>
          )}
          
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mt: 1, 
              color: Colors.primary, 
              cursor: 'pointer',
              textAlign: 'right'
            }}
            onClick={() => setSelectedCounty(null)}
          >
            
          </Typography>
        </Paper>
      )}

      {/* Legend */}
      {legendTitle && legendColors && legendValues && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            padding: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '4px',
            maxWidth: '180px',
            zIndex: 1000,
            fontSize: '0.85rem'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.9rem' }}>
            {legendTitle}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {legendColors.map((color, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    mr: 1
                  }} 
                />
                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                  {legendValues[index]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Hover Tooltip */}
      {hoverContent && !selectedCounty && (
        <Box
          sx={{
            position: 'fixed',
            top: tooltipPosition.y + 10,
            left: tooltipPosition.x + 10,
            backgroundColor: 'white',
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000,
            pointerEvents: 'none',
            maxWidth: '250px'
          }}
        >
          <Typography variant="body2" dangerouslySetInnerHTML={{ __html: hoverContent }} />
        </Box>
      )}

      {children}
    </Box>
  );
};

export default BaseMap;